import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAllNodes,
  selectEdgeLookup,
  selectFilteredEdgeNodes,
  selectPaths,
} from './slice/mapSelectors';
import { edgeToGeoJson, FeatureLike } from '../utils/geoJsonUtils';
import { setLuminosity } from '../utils/colors';
import { useTheme } from '../helpers/hooks';
import { useMapLayer } from '../hooks/useMapLayer';
import { Node } from '@/types';

const getEdgeStyle = () => [
  {
    filter: ['<=', ['get', 'head'], 0],
    style: {
      'stroke-width': 10,
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
    },
  },
  {
    filter: ['>', ['get', 'head'], 0],
    style: {
      'shape-points': 3,
      'shape-radius': 12,
      'shape-fill-color': ['get', 'color'],
      'shape-stroke-color': ['get', 'color'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
];

export const MapPaths = ({ zIndex }: { zIndex: number }) => {
  const [theme] = useTheme();
  const paths = useSelector(selectPaths);
  const edgeLookup = useSelector(selectEdgeLookup);
  const nodes = useSelector(selectAllNodes);
  const visibleNodes = useSelector(selectFilteredEdgeNodes);

  const features = useMemo(() => {
    if (!edgeLookup) return [];
    return paths
      .flatMap((path) => {
        const color = path.color;
        const ghostColor = setLuminosity(color, theme === 'light' ? 80 : 30);
        const pathNodes = path.nodes
          .map((nodeId) => nodes[nodeId])
          .filter(Boolean);
        const { dimWhenFiltered = true, arrowHeads = true } =
          path.options || {};

        if (pathNodes.length < 2) {
          return null;
        }

        const segments = dimWhenFiltered
          ? pathNodes.reduce<[Node[], boolean][]>((acc, node, index) => {
              if (index === 0) {
                acc.push([[node], Boolean(visibleNodes[node.id])]);
              } else {
                const lastSegment = acc[acc.length - 1];
                const lastNodeVisible = lastSegment[1];
                const currentNodeVisible = Boolean(visibleNodes[node.id]);
                acc[acc.length - 1][0].push(node);
                if (lastNodeVisible !== currentNodeVisible) {
                  const lastNode = lastSegment[0][lastSegment[0].length - 1];
                  acc.push([[lastNode, node], currentNodeVisible]);
                }
              }
              return acc;
            }, [])
          : ([[pathNodes, true]] as [Node[], boolean][]);

        return segments
          .flatMap(([nodes, visible], index) => {
            const showArrowHeads = arrowHeads && index === segments.length - 1;

            return edgeToGeoJson(
              {
                positions: nodes.map((node) => [
                  Number(node.lat),
                  Number(node.lng),
                ]),
              },
              () => ({
                key: `path-${path.id}-segment-${index}`,
                id: path.id,
                color: visible ? color : ghostColor,
                ...path.getProperties?.(visible),
              }),
              showArrowHeads,
            );
          })
          .filter(Boolean) as FeatureLike[];
      })
      .filter(Boolean) as FeatureLike[];
  }, [paths, edgeLookup, nodes, visibleNodes, theme]);

  const style = useMemo(() => getEdgeStyle(), []);

  useMapLayer(features, { zIndex, style });

  return <></>;
};
